import React, { useCallback, useEffect, useState } from 'react';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { LoginProvider } from '../contexts/LoginContext';
import { GOOGLE_CLIENT_ID } from '../config/config';
import LandingPage from './landing/LandingPage';
import { TermsPage, PrivacyPage, CookiePolicyPage } from './legal/LegalPages';
import McpPage from './mcp/McpPage';
import { claimAccess, getPendingInviteCode, clearPendingInviteCode } from '../services/access';
import './AccessGate.css';

/**
 * Invitation gate (issue #80).
 *
 * QuantApe is invite-only, so an anonymous visitor never reaches the app on any
 * route. This sits ABOVE <App/> rather than inside it so that the app's data
 * hooks — dozens of queries — never mount for someone who is only going to see
 * the landing page.
 *
 * The two branches deliberately never render at the same time, which is why
 * each can carry its own GoogleOAuthProvider/LoginProvider without the two
 * nesting: App keeps the providers it already had, and the gate supplies its
 * own for the landing page's sign-in button.
 */

const BootSplash = () => (
  <div className="gate-splash" role="status" aria-label="Loading">
    <div className="gate-splash-mark">
      <img src={`${process.env.PUBLIC_URL || ''}/quantape.png`} alt="" width="44" height="44" />
    </div>
  </div>
);

// A returning OAuth redirect carries the token in the hash. Start in "checking"
// when one is present so the landing page doesn't flash before the login
// callback has had a chance to run.
const hasOAuthRedirect = () =>
  typeof window !== 'undefined' && window.location.hash.includes('access_token');

const AccessGate = ({ children }) => {
  const [status, setStatus] = useState(() => (hasOAuthRedirect() ? 'checking' : 'unknown'));
  const [notice, setNotice] = useState('');

  // Validate any existing session before deciding which side of the gate to show.
  useEffect(() => {
    let cancelled = false;

    const check = async () => {
      if (hasOAuthRedirect()) return; // the login callback resolves this instead

      const token = localStorage.getItem('authToken');
      if (!token) {
        if (!cancelled) setStatus('anon');
        return;
      }

      try {
        const response = await fetch('https://api.1stprinciples.pro/auth/validateToken', {
          method: 'POST',
          body: token,
        });
        if (cancelled) return;

        if (response.ok) {
          const user = await response.json();
          if (user?.email) {
            setStatus('authed');
            return;
          }
        }
        localStorage.removeItem('authToken');
        setStatus('anon');
      } catch {
        // A network blip should not eject a signed-in user onto the landing
        // page; App re-validates on its own and handles a genuinely dead token.
        if (!cancelled) setStatus(token ? 'authed' : 'anon');
      }
    };

    check();
    return () => {
      cancelled = true;
    };
  }, []);

  // Signing out anywhere inside the app returns the visitor to the landing
  // page. Two events feed this: 'auth:logout' fires when authenticatedFetch
  // sees an expired/invalid token, 'auth:signed-out' when the user logs out
  // deliberately (App dispatches that one; see handleLogout).
  useEffect(() => {
    const onLogout = () => {
      setNotice('');
      setStatus('anon');
    };
    window.addEventListener('auth:logout', onLogout);
    window.addEventListener('auth:signed-out', onLogout);
    return () => {
      window.removeEventListener('auth:logout', onLogout);
      window.removeEventListener('auth:signed-out', onLogout);
    };
  }, []);

  /**
   * Post-Google handoff. The session token exists by now (LoginContext stored
   * it), so ask the server whether this identity may enter — redeeming the code
   * parked before the redirect, if there is one.
   */
  const handleGateLogin = useCallback(async (userData) => {
    setStatus('checking');
    const token = localStorage.getItem('authToken');
    const inviteCode = getPendingInviteCode();

    try {
      const result = await claimAccess({ token, inviteCode, name: userData?.name });

      if (result?.allowed) {
        clearPendingInviteCode();
        setNotice('');
        setStatus('authed');
        return;
      }

      // Refused: drop the half-made session so a reload doesn't look signed in.
      clearPendingInviteCode();
      localStorage.removeItem('authToken');
      localStorage.removeItem('userId');
      setNotice(
        result?.message ||
          'QuantApe is invitation only. Enter a code, or request an invitation below.'
      );
      setStatus('anon');
    } catch {
      setNotice('We could not verify your invitation just now. Please try again.');
      setStatus('anon');
    }
  }, []);

  if (status === 'checking' || status === 'unknown') {
    return <BootSplash />;
  }

  if (status === 'authed') {
    return children;
  }

  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <LoginProvider
        onLoginSuccess={handleGateLogin}
        onLoginFailure={(error) => {
          console.error('[INVITE] Google sign-in failed:', error);
          setStatus('anon');
        }}
      >
        <Router>
          <Routes>
            {/* Legal pages stay reachable without an invitation. */}
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/cookies" element={<CookiePolicyPage />} />
            <Route path="/mcp" element={<McpPage />} />
            <Route path="/*" element={<LandingPage notice={notice} />} />
          </Routes>
        </Router>
      </LoginProvider>
    </GoogleOAuthProvider>
  );
};

export default AccessGate;
